import React, { useEffect, useRef, useState } from 'react';
import { IoInformationCircleOutline, IoPeople, IoCopyOutline, IoCheckmark } from 'react-icons/io5';
import logo from '../../images/icon_small.png';
import { imageSrc } from '../../utils/avatar';
import { meetingLink } from '../../api/MeetingAPI';
import { useLanguage } from '../../i18n/LanguageContext';

// Clock, meeting identity, and the thing people actually come back to the top
// bar for: the link, so a host can invite one more person without leaving the
// call.
export default function MeetingTopBar({ meeting, participantCount, onOpenParticipants }) {
    const { t, locale } = useLanguage();
    const [now, setNow] = useState(() => new Date());
    const [infoOpen, setInfoOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const infoRef = useRef(null);

    // Ticks once a minute, aligned to the next minute boundary rather than a
    // flat 60s from mount, so the displayed time changes when the real clock
    // does instead of up to 59 seconds late.
    useEffect(() => {
        let timeoutId;
        const schedule = () => {
            const current = new Date();
            setNow(current);
            timeoutId = setTimeout(schedule, 60000 - (current.getSeconds() * 1000 + current.getMilliseconds()));
        };
        schedule();
        return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
        if (!infoOpen) return undefined;
        const close = (event) => {
            if (infoRef.current && !infoRef.current.contains(event.target)) setInfoOpen(false);
        };
        document.addEventListener('mousedown', close, true);
        return () => document.removeEventListener('mousedown', close, true);
    }, [infoOpen]);

    const link = meetingLink(meeting.code);

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (error) {
            // Clipboard access can be refused (an insecure origin, or a
            // browser that wants a fresher user gesture). Selecting the text
            // for the user is the next best thing to copying it for them.
            const field = infoRef.current && infoRef.current.querySelector('input');
            if (field) field.select();
        }
    };

    return (
        <header className="MeetTopBar glass">
            <div className="MeetTopLeft">
                <span className="MeetTopBrand">
                    <img src={logo} alt="Cyclone" className="MeetTopLogo BrandFallbackImg" />
                    {/* The group this meeting was started from, beside the
                        Cyclone mark - so a guest who has never seen the app
                        still knows whose room they walked into. Absent for a
                        meeting started outside any group, and for a group that
                        never uploaded an icon (the backend sends null rather
                        than the 'group.png' sentinel). */}
                    {meeting.groupImageUrl && (
                        <>
                            <span className="MeetTopBrandSep" />
                            <img
                                src={imageSrc(meeting.groupImageUrl)}
                                alt={meeting.groupName || ''}
                                className="MeetTopGroupLogo"
                                title={meeting.groupName || ''}
                            />
                        </>
                    )}
                </span>
                <span className="MeetTopClock">{now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</span>
                <span className="MeetTopDivider">|</span>
                <span className="MeetTopTitleWrap">
                    <span className="MeetTopTitle" title={meeting.title}>{meeting.title}</span>
                    {meeting.groupName && <span className="MeetTopGroupName">{meeting.groupName}</span>}
                </span>

                <div className="MeetTopInfoWrap" ref={infoRef}>
                    <button className="MeetTopIconBtn" onClick={() => setInfoOpen((open) => !open)} title={t('meeting.info')}>
                        <IoInformationCircleOutline />
                    </button>
                    {infoOpen && (
                        <div className="MeetTopInfoPopover glass">
                            <p className="MeetTopInfoLabel">{t('meeting.shareLink')}</p>
                            {/* readOnly rather than disabled: a disabled input
                                can't be selected, which is the whole fallback
                                when the clipboard API is unavailable. */}
                            <input className="MeetTopInfoLink" value={link} readOnly onFocus={(event) => event.target.select()} />
                            <button className="MeetTopInfoCopy primaryColor" onClick={copyLink}>
                                {copied ? <IoCheckmark /> : <IoCopyOutline />}
                                {copied ? t('meeting.copied') : t('meeting.copyLink')}
                            </button>
                            <p className="MeetTopInfoCode">{t('meeting.lobby.code', { code: meeting.code })}</p>
                        </div>
                    )}
                </div>
            </div>

            <button className="MeetTopCount" onClick={onOpenParticipants} title={t('meeting.participants')}>
                <IoPeople />
                <span>{participantCount}</span>
            </button>
        </header>
    );
}
